import { useEffect, useState } from 'react';

import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { flowRight as compose, capitalize } from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';

import JLoading from '../JLoading';
import TimeWindow from '../TimeWindow';
import CountdownTimer from './CountdownTimer';
import styles from './styles';

const NO_SLOTS_MESSAGE =
  'There are no available pickup window slots currently. \nPlease contact Transportation for further support.';

// Normalize a time value (ms number or ISO string) to a comparable timestamp.
const toTs = (v) => (v == null ? null : moment(v).valueOf());

// True when an array item's start/end equal the current window's, format-agnostic.
const matchesCurrentWindow = (w, current) => {
  if (!w || !current) return false;
  return toTs(w.start_ts) === toTs(current.start_ts) && toTs(w.end_ts) === toTs(current.end_ts);
};

function SelectPickupWindow(props) {
  const { store, assignment, driver, currentWindow, selectedWindowId, onSelectWindow, onTimeout, countdownSeconds } = props;
  const { assignmentStore } = store;

  const [windows, setWindows] = useState([]);
  const [windowsLoading, setWindowsLoading] = useState(true);

  // Load the available pickup windows
  useEffect(() => {
    if (!assignment?.id) return undefined;

    let active = true;
    setWindowsLoading(true);

    assignmentStore
      .loadPickupWindows(assignment.id, driver?.id)
      .then((res) => {
        if (!active) return;
        const list = res?.ok && Array.isArray(res.data) ? res.data : [];
        setWindows(list);
        setWindowsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setWindows([]);
        setWindowsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [assignmentStore, assignment?.id, driver?.id]);


  useEffect(() => {
    if (windowsLoading || !currentWindow) return;
    const validIds = new Set([currentWindow.id, ...windows.map((w) => w.id)].map(String));
    if (selectedWindowId == null || !validIds.has(String(selectedWindowId))) {
      onSelectWindow(currentWindow.id);
    }
  }, [currentWindow, selectedWindowId, windows, windowsLoading, onSelectWindow]);

  const driverName = driver ? `${capitalize(driver.first_name)} ${capitalize(driver.last_name)}` : '-';

  // Merge the current window and filter out any duplicate window from the list
  // and present every option sorted by start time.
  const displayWindows = [
    ...(currentWindow ? windows.filter(w => !matchesCurrentWindow(w, currentWindow)) : windows),
    ...(currentWindow ? [currentWindow] : []),
  ].sort((a, b) => toTs(a.start_ts) - toTs(b.start_ts));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box>
          <Box py={1}>
            <strong>
              Assignment {assignment?.label} - {assignment?.shipment_count} Shipments 
              {assignment?.warehouse_id ? ` - Warehouse #${assignment.warehouse_id}` : ''}
              {assignment?.region_code ? ` - ${assignment.region_code}` : ''}
            </strong>
          </Box>
          <Box py={0.5} sx={styles.grayText}>
            <small>Driver: {driverName}</small>
          </Box>
        </Box>
        <CountdownTimer active={!windowsLoading} onExpire={onTimeout} durationSeconds={countdownSeconds} />
      </Box>

      <Box sx={styles.listWrapper}>
        {windowsLoading ? (
          <Box py={3}>
            <JLoading />
          </Box>
        ) : displayWindows.length > 0 ? (
          <RadioGroup
            name="pickup-window"
            value={selectedWindowId || ''}
            onChange={(e) => onSelectWindow(e.target.value)}
          >
            {displayWindows.map((window) => (
              <FormControlLabel
                key={window.id}
                value={window.id}
                control={<Radio color="primary" size="small" />}
                label={
                  <TimeWindow
                    variant="inline"
                    startTs={window.start_ts}
                    endTs={window.end_ts}
                    timezone={assignment?.timezone}
                  />
                }
              />
            ))}
          </RadioGroup>
        ) : (
          <Box sx={styles.emptyMessage}>
            <Typography color='error' variant="body2" align="center">
              {NO_SLOTS_MESSAGE}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const SelectPickupWindowCompose = compose(inject('store'), observer)(SelectPickupWindow);

export default SelectPickupWindowCompose;
